import {useState, useEffect, type MutableRefObject} from 'react';
import {mutate} from 'swr';
import {type Site, type SiteMab, type WriteSite} from 'client';

import {useInputMenuContext} from 'context/InputMenu';

import useParseURL from 'hooks/useParseURL';

import addEditSiteBackendHandler, {siteInitialState} from 'helpers/addEdit/site';

import {SWRKeyEnum} from 'interfaces';
import {useGetRegions, useGetSite} from 'hooks/swr';
import {useTranslation} from 'react-i18next';

type FormSite = Partial<Omit<WriteSite, 'mab'> & {mab?: Array<Partial<SiteMab>>}>;

function useAddEditSite(isMounted: MutableRefObject<boolean>) {
  const {planId} = useParseURL();
  const {t} = useTranslation('common');

  const inputStateContext = useInputMenuContext();
  const inputMenuDispatch = inputStateContext?.dispatch;
  const inputMenuState = inputStateContext?.state;
  const operationAddEdit = inputMenuState?.method;

  const siteId = inputMenuState?.formData?.id;

  const {data: readSite} = useGetSite({resourceId: siteId});

  const defaultSiteValue = siteId ? undefined : siteInitialState(undefined);
  const [site, setSite] = useState<FormSite | undefined>(defaultSiteValue);
  const [createUpdateSite, setCreateUpdateSite] = useState<boolean>(false);
  const {data: regions} = useGetRegions({resourceId: planId});

  const communicationSuccess = (site: Site) => {
    setCreateUpdateSite(false);
    const init = siteInitialState(undefined);
    setSite(init);

    void mutate(planId);
    void mutate([planId, SWRKeyEnum.GET_PLAN_SITES]);
    void mutate([site.id, SWRKeyEnum.GET_SITE]);
    void mutate(site.id);
    void mutate([planId, SWRKeyEnum.GET_PLAN_TREE_NAVIGATION]);
    void mutate([planId, SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
  };

  const isSiteFormValid = () => {
    if (!site?.name) {
      return false;
    }
    return site?.mab ? site.mab.every(({limit, fromDate}) => limit !== undefined && !!fromDate) : true;
  };

  useEffect(() => {
    setSite(siteInitialState(readSite));
  }, [readSite]);

  useEffect(() => {
    if (regions !== undefined && regions[0] && site?.regionId === '') {
      setSite({...site, regionId: regions[0].id});
    }
  }, [regions, site !== undefined]);

  useEffect(() => {
    const isSiteValid = isSiteFormValid();

    operationAddEdit &&
      inputMenuDispatch &&
      site &&
      isSiteValid &&
      addEditSiteBackendHandler({
        planId,
        site: site as WriteSite,
        createUpdateSite,
        setCreateUpdateSite,
        operationAddEdit,
        inputMenuDispatch,
        siteId,
        communicationSuccess,
        t,
        isMounted,
      });
  }, [createUpdateSite, site, planId, operationAddEdit]);

  return {site, setSite, createUpdateSite, setCreateUpdateSite, isSiteFormValid, siteId};
}

export default useAddEditSite;
